const CACHE_KEY = 'sp_device_fp';

// Order and separators are mirrored by DeviceFingerprintSignals#canonicalForm in gateway-backend.
// Changing either side alone makes every server side hash comparison fail.
const CANONICAL_FIELDS = [
  'ua',
  'languages',
  'timezone',
  'screen',
  'colorDepth',
  'hardwareConcurrency',
  'deviceMemory',
  'touchPoints',
  'platform',
  'webdriver',
  'canvasHash',
  'webglHash',
  'fontsHash',
  'visitorId',
];

let fingerprintPromise = null;

function canonicalForm(signals) {
  return CANONICAL_FIELDS.map((field) => {
    const value = signals[field];
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join(',');
    if (typeof value === 'boolean') return String(value);
    return String(value);
  }).join('|');
}

async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text);
  if (window.crypto?.subtle) {
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // Insecure contexts have no SubtleCrypto; a weaker digest still yields a stable identifier.
  let hash = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i += 1) {
    hash ^= bytes[i];
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function base64Url(text) {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function canvasHash() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 60;
    const context = canvas.getContext('2d');
    if (!context) return '';
    context.textBaseline = 'top';
    context.font = '14px "Arial"';
    context.fillStyle = '#f60';
    context.fillRect(10, 10, 90, 30);
    context.fillStyle = '#069';
    context.fillText('swirepay-device-check', 12, 14);
    context.fillStyle = 'rgba(102, 204, 0, 0.7)';
    context.fillText('swirepay-device-check', 14, 20);
    return sha256Hex(canvas.toDataURL());
  } catch {
    return '';
  }
}

async function webglHash() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const parts = [
      gl.getParameter(gl.VERSION),
      gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      gl.getParameter(gl.VENDOR),
      gl.getParameter(gl.RENDERER),
      gl.getParameter(gl.MAX_TEXTURE_SIZE),
      debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : '',
      debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : '',
    ];
    return sha256Hex(parts.join('~'));
  } catch {
    return '';
  }
}

async function visitorSignals() {
  try {
    // Loaded on demand so the agent never sits in the initial bundle.
    const { default: FingerprintJS } = await import('@fingerprintjs/fingerprintjs');
    const agent = await FingerprintJS.load();
    const result = await agent.get();
    const fonts = result.components?.fontPreferences?.value ?? result.components?.fonts?.value;
    return {
      visitorId: result.visitorId ?? '',
      fontsHash: fonts ? await sha256Hex(JSON.stringify(fonts)) : '',
    };
  } catch {
    return { visitorId: '', fontsHash: '' };
  }
}

async function collectSignals() {
  const [canvas, webgl, visitor] = await Promise.all([canvasHash(), webglHash(), visitorSignals()]);
  return {
    ua: navigator.userAgent ?? '',
    languages: Array.from(navigator.languages ?? []),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    colorDepth: String(window.screen?.colorDepth ?? ''),
    hardwareConcurrency: String(navigator.hardwareConcurrency ?? ''),
    deviceMemory: String(navigator.deviceMemory ?? ''),
    touchPoints: String(navigator.maxTouchPoints ?? 0),
    platform: navigator.platform ?? '',
    webdriver: navigator.webdriver === true,
    canvasHash: canvas,
    webglHash: webgl,
    fontsHash: visitor.fontsHash,
    visitorId: visitor.visitorId,
  };
}

async function buildFingerprint() {
  const signals = await collectSignals();
  return {
    fingerprint: await sha256Hex(canonicalForm(signals)),
    signals: base64Url(JSON.stringify(signals)),
    webdriver: signals.webdriver,
  };
}

function readCache() {
  try {
    const cached = window.sessionStorage?.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function writeCache(value) {
  try {
    window.sessionStorage?.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // Private browsing can reject writes; recomputing per request is an acceptable cost.
  }
}

/**
 * Resolves the device fingerprint for the current tab. Never rejects: a checkout must not fail
 * because fingerprinting did, the server decides what to do with a degraded bundle.
 */
export async function getDeviceFingerprint() {
  const cached = readCache();
  if (cached?.fingerprint) return cached;

  if (!fingerprintPromise) {
    fingerprintPromise = buildFingerprint()
      .then((value) => {
        writeCache(value);
        return value;
      })
      .catch((err) => {
        console.error('device fingerprint failed', err);
        fingerprintPromise = null;
        return { fingerprint: '', signals: '', webdriver: navigator.webdriver === true };
      });
  }
  return fingerprintPromise;
}

/** Warms the fingerprint so the first Pay click does not pay for canvas and font probing. */
export function preloadDeviceFingerprint() {
  return getDeviceFingerprint();
}
